import { createAsyncThunk } from '@reduxjs/toolkit';
import { wsMmSend } from '@skynet/data-ws-mm';
import { selectRestoringLoanId, selectRestoringProjectId } from './selectors';
import type { LoansArchiveStore, RetainersArchiveStore } from './selectors';
import { LoanArchiveCommands, RetainerArchiveCommands } from './types';
import type {
  SearchArchivedLoansPayload,
  SearchArchivedRetainersPayload,
  ShowLoanPayload,
  ShowRetainerPayload,
} from './types';

// Plain `createAsyncThunk` with a narrow `state` view instead of the app's
// `createAppAsyncThunk`: this package must not depend on the app's `RootState`.
// The thunks only dispatch a WS send, which is a synchronous action creator,
// so the dispatch type needs no narrowing.
const createLoansThunk = createAsyncThunk.withTypes<{ state: LoansArchiveStore }>();
const createRetainersThunk = createAsyncThunk.withTypes<{ state: RetainersArchiveStore }>();

export const searchArchivedLoans = createLoansThunk(
  'loans/searchArchivedLoans',
  (data: SearchArchivedLoansPayload, thunkApi) => {
    thunkApi.dispatch(
      wsMmSend({
        command: LoanArchiveCommands.SearchArchivedLoans,
        ...data,
      }),
    );
  },
);

/**
 * Restores an archived loan — the counterpart of `hideLoan`.
 *
 * The `loans/archive` slice reacts to this thunk's whole lifecycle to track
 * which row is being restored. Dispatch it only to restore an archived loan:
 * any other caller would show the archive page a restore it never started.
 *
 * Refuses to run while another restore is still waiting for its ack — see
 * `condition` below.
 */
export const showLoan = createLoansThunk(
  'loans/showLoan',
  (data: ShowLoanPayload, thunkApi) => {
    thunkApi.dispatch(
      wsMmSend({
        command: LoanArchiveCommands.ShowLoan,
        ...data,
      }),
    );
  },
  {
    /**
     * The `ShowLoan` ack does not echo the loan id back, so the archive slice
     * can only match it against the one restore it recorded. A second command
     * sent on top of that would be acked into the wrong row, which is why this
     * is gated here rather than in the reducer: `pending` is dispatched before
     * the payload creator runs, so a reducer that refuses the second request
     * still lets its command go out. Returning `false` here dispatches nothing
     * at all — no `pending`, no `rejected`, no send.
     */
    condition: (_data: ShowLoanPayload, { getState }) => selectRestoringLoanId(getState()) === null,
  },
);

export const searchArchivedRetainers = createRetainersThunk(
  'retainers/searchArchivedRetainers',
  (data: SearchArchivedRetainersPayload, thunkApi) => {
    thunkApi.dispatch(
      wsMmSend({
        command: RetainerArchiveCommands.SearchArchivedRetainers,
        ...data,
      }),
    );
  },
);

/**
 * Restores an archived project — the counterpart of `hideProject`.
 *
 * The `retainers/archive` slice reacts to this thunk's whole lifecycle to track
 * which row is being restored. Dispatch it only to restore an archived project:
 * any other caller would show the archive page a restore it never started.
 *
 * Refuses to run while another restore is still waiting for its ack — see
 * `condition` below.
 */
export const showRetainer = createRetainersThunk(
  'retainers/showRetainer',
  (data: ShowRetainerPayload, thunkApi) => {
    thunkApi.dispatch(
      wsMmSend({
        command: RetainerArchiveCommands.ShowRetainer,
        ...data,
      }),
    );
  },
  {
    /**
     * The `ShowRetainer` ack does not echo the project id back, so the archive
     * slice can only match it against the one restore it recorded. A second
     * command sent on top of that would be acked into the wrong row, which is
     * why this is gated here rather than in the reducer: `pending` is dispatched
     * before the payload creator runs, so a reducer that refuses the second
     * request still lets its command go out. Returning `false` here dispatches
     * nothing at all — no `pending`, no `rejected`, no send.
     */
    condition: (_data: ShowRetainerPayload, { getState }) =>
      selectRestoringProjectId(getState()) === null,
  },
);
